import {
  displayCurrency,
  displayDistributions,
  sumAllDistributions,
  sumAllSurcharges,
} from 'app/scripts/PricingHelper';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import ObjectInfo from 'assets/templates/objectInfo/ObjectInfo';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { isEmpty } from 'lodash';
import { useSalesContractProvider } from '../../Sales.Contract.Hooks';

export default function ContractInfoCancellation() {
  const { lang } = getLocales();
  const { isDealerOrDistributor, isDealer } = useAuthStore();
  const { subfeeNameMap, contractCancellation, selected } = useSalesContractProvider();

  if (!contractCancellation && !selected) return null;

  const effective = {
    // basic info
    cancellationDateLocal: contractCancellation?.cancellationDateLocal ?? selected?.cancellationDate,
    reason: contractCancellation?.reason ?? selected?.cancellationReason,
    reasonDescription: contractCancellation?.reasonDescription ?? selected?.cancellationReasonDescription,
    odometerReading: contractCancellation?.odometerReading ?? selected?.cancellationOdometerReading,
    refundDateAtLocal: contractCancellation?.refundDateAtLocal ?? selected?.refundDateAtLocal,

    // distributions & prices
    refundPricingDistributions:
      contractCancellation?.refundPricingDistributions ?? selected?.refundPricingDistributions,
    refundDistributorDistributions:
      contractCancellation?.refundDistributorDistributions ?? selected?.refundDistributorDistributions,
    refundSubfees: contractCancellation?.refundSubfees ?? selected?.refundSubfees,
    refundEnhancements: contractCancellation?.refundEnhancements ?? selected?.refundEnhancements,
  };

  const totalSurcharges = sumAllSurcharges(effective.refundSubfees);
  const totalPricingDistributions = sumAllDistributions(effective.refundPricingDistributions);
  const totalDistributorDistributions = sumAllDistributions(effective.refundDistributorDistributions);

  const enhancementRefundPrices = effective.refundEnhancements?.map((it) => ({
    totalPricingDistributions: sumAllDistributions(it.pricingDistributions),
    totalDistributorDistributions: sumAllDistributions(it.distributorDistributions),
  }));

  const totalEnhancements = enhancementRefundPrices?.reduce((acc, it) => acc + it.totalPricingDistributions, 0) ?? 0;

  const totalEnhancementsDistributor =
    enhancementRefundPrices?.reduce((acc, it) => acc + it.totalDistributorDistributions, 0) ?? 0;

  const grandTotal =
    totalSurcharges +
    totalPricingDistributions +
    totalDistributorDistributions +
    totalEnhancements +
    totalEnhancementsDistributor;

  return (
    <>
      <h1>{lang.cancellationInfo}</h1>

      {effective.cancellationDateLocal && (
        <div>
          {lang.cancellationDate}: <b>{localizeDate(effective.cancellationDateLocal, defaultDateFormat)}</b>
        </div>
      )}

      {effective.reason && (
        <div>
          {lang.cancellationReason}: <b>{effective.reason}</b>
        </div>
      )}

      {effective.odometerReading && (
        <div>
          {lang.odometerReading}:{' '}
          <b>
            <FormatValue value={`${effective.odometerReading}km`} />
          </b>
        </div>
      )}

      {effective.reasonDescription && (
        <div>
          {lang.cancellationReasonDescription}: <b>{effective.reasonDescription}</b>
        </div>
      )}

      {effective.refundDateAtLocal && (
        <>
          <h1>{lang.cancellationInfo}</h1>
          <div>
            {lang.refundDate}: <b>{localizeDate(effective.refundDateAtLocal, defaultDateFormat)}</b>
          </div>
        </>
      )}

      {!isDealerOrDistributor && !isEmpty(effective.refundPricingDistributions) && (
        <>
          <ObjectInfo
            object={displayDistributions(effective.refundPricingDistributions)}
            objectName={lang.pricingDistributions}
            disableTranslation
          />
          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.pricingDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalPricingDistributions)} />
            </b>
          </div>
        </>
      )}

      {!isDealer && !isEmpty(effective.refundDistributorDistributions) && (
        <>
          <ObjectInfo
            object={displayDistributions(effective.refundDistributorDistributions)}
            objectName={lang.distributorDistributions}
            disableTranslation
          />
          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.distributorDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalDistributorDistributions)} />
            </b>
          </div>
        </>
      )}

      {!isEmpty(subfeeNameMap) && !isEmpty(effective.refundSubfees) && (
        <>
          <h3>{lang.surcharges}</h3>
          <DataMap
            data={effective.refundSubfees}
            render={({ data: subfee }) => (
              <div key={subfee?.subfeeId}>
                {subfeeNameMap[subfee?.subfeeId?.toString()] || lang.unknown}:{' '}
                <b>
                  {subfee?.quantity} x <FormatValue value={displayCurrency(subfee?.amount || 0, subfee?.currency)} />
                </b>
              </div>
            )}
          />
          <hr />
          <div style={{ marginTop: '0.5rem' }}>
            {lang.total} {lang.surcharges.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalSurcharges)} />
            </b>
          </div>
        </>
      )}

      {!isEmpty(effective.refundEnhancements) && (
        <>
          <h1>{lang.enhancementsDistributions}</h1>

          {effective.refundEnhancements.map((enh, idx) => {
            return (
              <div key={idx}>
                <h2 style={{ marginTop: '2rem', marginBottom: '0.5rem' }}>{enh.name}</h2>

                {!isEmpty(enh.pricingDistributions) && (
                  <ObjectInfo
                    object={displayDistributions(enh.pricingDistributions)}
                    objectName={lang.pricingDistributions}
                    disableTranslation
                  />
                )}
                {!isEmpty(enh.distributorDistributions) && (
                  <ObjectInfo
                    object={displayDistributions(enh.distributorDistributions)}
                    objectName={lang.distributorDistributions}
                    disableTranslation
                  />
                )}
              </div>
            );
          })}

          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.pricingDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalEnhancements)} />
            </b>
          </div>

          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.distributorDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalEnhancementsDistributor)} />
            </b>
          </div>
        </>
      )}

      {!!grandTotal && (
        <div>
          <h2>{lang.total}</h2>
          <hr />
          <b>
            <FormatValue value={displayCurrency(grandTotal)} />
          </b>
        </div>
      )}
    </>
  );
}
